/**
 * SignInButton Component
 * Renders a sign-in button that opens the custom auth modal
 * Hidden when user is authenticated
 */

import { useAuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../hooks/useTheme';

/**
 * SignInButton component
 * Displays a sign-in link that redirects to Cognito for authentication
 * Automatically hidden when user is authenticated
 * 
 * @returns Sign-in button component or null if authenticated
 */
export function SignInButton() {
  const { isAuthenticated, signIn, isLoading } = useAuthContext();
  const { theme } = useTheme();

  // Hide when authenticated or loading
  if (isAuthenticated || isLoading) {
    return null;
  }

  const isRetro = theme === 'retro';

  return (
    <button
      onClick={signIn}
      aria-label="Sign in"
      className="sign-in-button"
      style={{
        background: isRetro ? '#00ff00' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        color: isRetro ? '#000000' : 'white',
        border: isRetro ? '3px solid #00ff00' : 'none',
        padding: '0.5rem 1.25rem',
        borderRadius: isRetro ? '0' : '6px',
        fontSize: '1rem',
        fontWeight: isRetro ? '700' : '600',
        fontFamily: 'var(--font-family)',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isRetro ? '4px 4px 0 #00aa00' : '0 2px 8px rgba(99, 102, 241, 0.3)',
        position: 'relative',
        overflow: 'hidden',
        textTransform: isRetro ? 'uppercase' : 'none',
      }}
      onMouseEnter={(e) => {
        if (isRetro) {
          e.currentTarget.style.background = '#00aa00';
          e.currentTarget.style.boxShadow = '6px 6px 0 #007700';
        } else {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(99, 102, 241, 0.5)';
        }
      }}
      onMouseLeave={(e) => {
        if (isRetro) {
          e.currentTarget.style.background = '#00ff00';
          e.currentTarget.style.boxShadow = '4px 4px 0 #00aa00';
        } else {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.3)';
        }
      }}
      onMouseDown={(e) => {
        if (isRetro) {
          e.currentTarget.style.transform = 'translate(2px, 2px)';
          e.currentTarget.style.boxShadow = '2px 2px 0 #00aa00';
        } else {
          e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
        }
      }}
      onMouseUp={(e) => {
        if (isRetro) {
          e.currentTarget.style.transform = 'translate(0, 0)';
          e.currentTarget.style.boxShadow = '6px 6px 0 #007700';
        } else {
          e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
        }
      }}
    >
      Sign In
    </button>
  );
}
